import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import { api, setCreatorCsrf } from '../api/client'
import type { MeResponse } from '../api/types'
import { ApiError } from '../api/types'

type AuthState = {
  user: MeResponse | null
  loading: boolean
  refresh: () => Promise<MeResponse | null>
  logout: () => Promise<void>
}

const AuthContext = createContext<AuthState | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<MeResponse | null>(null)
  const [loading, setLoading] = useState(true)

  const refresh = useCallback(async (): Promise<MeResponse | null> => {
    try {
      const me = await api.me()
      setUser(me)
      return me
    } catch (err) {
      setUser(null)
      setCreatorCsrf(null)
      if (err instanceof ApiError && (err.status === 401 || err.status === 403)) {
        return null
      }
      return null
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void refresh()
  }, [refresh])

  const logout = useCallback(async () => {
    try {
      await api.logout()
    } catch {
      // session may already be gone
    }
    setUser(null)
    setCreatorCsrf(null)
  }, [])

  const value = useMemo(
    () => ({
      user,
      loading,
      refresh,
      logout,
    }),
    [user, loading, refresh, logout],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth requires AuthProvider')
  return ctx
}
